import {
	addSelected,
	changeFilterSelect,
	changePage,
	changeRiskConfigPublishState,
	changeSearch,
	changeSlice,
	changeSort,
	clearRiskConfig,
	clearSelected,
	renameRiskCount,
	createRiskConfig,
	currentRow,
	deleteRisk,
	duplicateRiskConfig,
	getLog,
	getLogRejected,
	getLogResolved,
	getRiskConfig,
	getRiskConfigRejected,
	getRiskConfigResolved,
	getRiskCountResolved,
	getRiskDetails,
	getRiskDetailsRejected,
	getRiskDetailsResolved,
	removeSelected,
	renameRiskConfigAction,
	saveRiskConfigAction,
	setRefreshList,
	setRiskCalcStatus,
	setRiskConfigIsCreating,
	setRiskConfigNameError,
	setRiskCountIsLoading,
	setRiskDetailsIsBusy,
	setRiskIsDeleting,
	setRiskIsSaving,
	setRiskSetupDirty,
	setRunCompleteReload,
	setSaveRun,
	setSelected,
	setTab,
	translate,
	updateColumnFilterSelectOptions,
	setUpdatedRanges,
	getSimulationDataResolved,
	setDidMatrixUpdate,
	setUpdatedScores,
	setRiskType,
	setIsPreviewLoading,
	// setRiskMatrixPreviewRanges,
	setRiskMatrixAfterPreview,
	setMatrixPreviewStatus,
	setRefreshMatrixGrid,
	getTaskDataResolved,
	setIsPreviewOnLoad,
	setEditedCellsList,
	setEnablePreview,
	setOnlyGradesUpdated,
	clearMatrixUpdate,
	setOverallRisk,
	resetRiskResolved,
	setCancellPreview,
	changePagination,
	clearRiskScanId,
	changeFiltersSelect,
} from '@Actions';
import { createReducer } from '@reduxjs/toolkit';
import {
	ComponentCountReply,
	LogReply,
	RiskConfig,
	RiskMatrixGridObj,
	RiskStoreState,
} from '@Types';
import { AnyAction } from 'redux';
import * as AM_COMMON from '@innovyze/lib_am_common/Reducers';
import { paginationInitialState } from '@innovyze/lib_am_common/Reducers';
import { riskDetailsInitialState } from './filterInitialState';
import { ConfigPublishState } from '@Types/riskConfig.types';
import { InspectionStandardType } from '@Types/InspectionStandardType';
import { defaultRiskMatrix } from './RiskMatrixDefaultScores';
import { Pagination } from '@innovyze/lib_am_common';

const configInitState: RiskConfig = {
	riskSetting: {
		publishState: ConfigPublishState.unpublished,
		inspectionStandard: InspectionStandardType.PACP,
		RiskMethod: 'multiplied',
		LOFWeighting: 50,
		COFWeighting: 50,
		Grades: {
			Low: 5,
			Medium: 10,
			High: 15,
			Extreme: 20,
			Max: 25,
		},
		LastRun: '',
		lastRunCompleted: '',
		configId: '',
		assetType: '',
		name: '',
		riskType: 'linear',
		matrix: { ...defaultRiskMatrix },
	},
	LOFCategories: [],
	COFCategories: [],
};

export const initState: RiskStoreState = {
	riskConfig: configInitState,
	isLoading: false,
	tab: '',
	riskDetails: paginationInitialState,
	search: '',
	saveRun: false,
	dropdownFilters: riskDetailsInitialState(''),
	isSetupDirty: false,
	runCompleteReload: false,
	riskConfigNameError: '',
	isCreating: false,
	isSaving: false,
	isDeleting: false,
	refreshList: false,
	isCountLoading: [],
	riskCount: {},
	simulationData: [],
	taskData: [],
	didMatrixUpdate: false,
	riskMatrix: { ...defaultRiskMatrix },
	enablePreview: false,
	cancelPreview: false,
	isPreviewLoading: false,
	previewStatus: '',
	previewObj: { grades: { lofRanges: [], cofRanges: [] }, grid: [] },
	refreshMatrixGrid: false,
	isPreviewOnLoad: false,
	editedCellsList: [],
	riskType: 'linear',
	isOnlyGradesUpdated: false,
	overallRisk: {
		riskMethod: configInitState.riskSetting.RiskMethod,
		lofWeighting: configInitState.riskSetting.LOFWeighting,
		cofWeighting: configInitState.riskSetting.COFWeighting,
	},
};

interface RiskReducer {
	[x: string]: (
		state: RiskStoreState,
		action: AnyAction,
	) => RiskStoreState | undefined;
}

export interface RiskCountChange {
	key: string;
	values?: ComponentCountReply[];
}

export interface RiskCountRename {
	existingKey: string;
	newKey: string;
}

export interface IsCountLoadingChange {
	key: string;
	loading: boolean;
}

const updateRiskCount = (state: RiskStoreState, action: RiskCountChange) => {
	if (action.values || state.riskCount[action.key])
		state.riskCount[action.key] = action.values;

	return state;
};

const copyRiskCount = (state: RiskStoreState, action: RiskCountRename) => {
	if (state.riskCount[action.existingKey]) {
		state.riskCount[action.newKey] = state.riskCount[action.existingKey];
		state.riskCount[action.existingKey] = undefined;
	}
	return state;
};

const updateIsCountLoading = (
	state: RiskStoreState,
	action: IsCountLoadingChange,
) => {
	if (action.loading && state.isCountLoading.indexOf(action.key) === -1)
		state.isCountLoading.push(action.key);
	else if (!action.loading && state.isCountLoading.indexOf(action.key) !== -1)
		state.isCountLoading = state.isCountLoading.filter(
			value => value !== action.key,
		);
};

export const reducer: RiskReducer = {
	[getRiskConfig.toString()]: state => ({
		...state,
		isLoading: true,
	}),
	[getRiskConfigResolved.toString()]: (state, action) => {
		const tempMatrix =
			action.payload.riskSetting?.matrix?.grades &&
			action.payload.riskSetting?.matrix?.grid?.length > 0
				? {
						grades: action.payload.riskSetting?.matrix?.grades,
						grid: action.payload.riskSetting?.matrix?.grid,
				  }
				: { ...defaultRiskMatrix };
		return {
			...state,
			riskConfig: action.payload,
			riskMatrix: { ...tempMatrix },
			riskType: action.payload.riskSetting?.riskType ?? 'linear',
			dropdownFilters: riskDetailsInitialState(
				(action.payload as RiskConfig).riskSetting.assetType,
			),
			previewStatus: '',
			isPreviewOnLoad: false,
			isPreviewLoading: false,
			isLoading: false,
			cofCfgCategories: action.payload.cofCfgCategories,
			lofCfgCategories: action.payload.lofCfgCategories,
		};
	},
	[getRiskConfigRejected.toString()]: state => ({
		...state,
		isLoading: false,
	}),
	[clearRiskConfig.toString()]: state => ({
		...state,
		riskConfig: configInitState,
		riskDetails: paginationInitialState,
		search: '',
		saveRun: false,
		dropdownFilters: riskDetailsInitialState(''),
		riskCount: {},
		isSetupDirty: false,
	}),
	[createRiskConfig.toString()]: state => ({
		...state,
		isCreating: true,
	}),
	[duplicateRiskConfig.toString()]: state => ({
		...state,
		isCreating: true,
	}),
	[changeRiskConfigPublishState.toString()]: (state, action) => ({
		...state,
		riskConfig: {
			...state.riskConfig,
			riskSetting: {
				...state.riskConfig.riskSetting,
				publishState: action.payload.newPublishState,
			},
		},
	}),
	[renameRiskConfigAction.toString()]: (state, action) => ({
		...state,
		...action.payload,
		isSaving: true,
	}),
	[saveRiskConfigAction.toString()]: (state, action) => ({
		...state,
		riskConfig: { ...action.payload },
		editedCellsList: [],
		isSaving: true,
	}),
	[setTab.toString()]: (state, action) => ({
		...state,
		tab: action.payload,
	}),
	[getSimulationDataResolved.toString()]: (state, action) => ({
		...state,
		simulationData: action.payload,
	}),
	[getTaskDataResolved.toString()]: (state, action) => ({
		...state,
		taskData: action.payload,
	}),
	[getRiskDetails.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updateGet(state.riskDetails, action.payload),
	}),
	[getRiskDetailsResolved.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updateResolved(
			state.riskDetails,
			action.payload,
		),
	}),
	[getRiskDetailsRejected.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updateRejected(
			state.riskDetails,
			action.payload,
		),
	}),
	[clearRiskScanId.toString()]: state => ({
		...state,
		riskDetails: { ...state.riskDetails, scanId: '' },
	}),
	[changePage.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updatePage(state.riskDetails, action.payload),
	}),
	[changeSlice.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updateSlice(state.riskDetails, action.payload),
	}),
	[changePagination.toString()]: (state, action) => {
		const query: Pagination = AM_COMMON.makePaginationQuery({
			page: action.payload?.page,
			slice: action.payload?.slice,
			pages: state.riskDetails.pagination.pages,
			total: state.riskDetails.pagination.total,
			subtotal: state.riskDetails.pagination.subtotal,
			search: state.riskDetails.pagination.search,
			sortKey: state.riskDetails.pagination.sortKey,
			sortDescending: state.riskDetails.pagination.sortDescending,
		});
		const pagination = {
			...state.riskDetails.pagination,
			slice:
				action.payload?.slice ?? state.riskDetails?.pagination?.slice,
			page: action.payload?.page ?? state.riskDetails?.pagination?.page,
		};

		return {
			...state,
			riskDetails: {
				...state.riskDetails,
				isWaiting: true,
				status: AM_COMMON.statusLoading(),
				pagination,
				query,
			},
		};
	},
	[changeSearch.toString()]: (state, action) => ({
		...state,
		search: action.payload,
		riskDetails: AM_COMMON.updateSearch(state.riskDetails, action.payload),
	}),
	[changeSort.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updateSort(state.riskDetails, action.payload),
	}),
	[clearSelected.toString()]: state => ({
		...state,
		riskDetails: AM_COMMON.setSelected(state.riskDetails, []),
	}),
	[setSelected.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.setSelected(state.riskDetails, action.payload),
	}),
	[addSelected.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.addSelected(state.riskDetails, action.payload),
	}),
	[removeSelected.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.removeSelected(
			state.riskDetails,
			action.payload,
		),
	}),
	[setRiskDetailsIsBusy.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.updateIsBusy(state.riskDetails, action.payload),
	}),
	[currentRow.toString()]: (state, action) => ({
		...state,
		riskDetails: AM_COMMON.setCurrentDbId(
			state.riskDetails,
			action.payload,
		),
	}),
	[setSaveRun.toString()]: (state, action) => ({
		...state,
		saveRun: action.payload,
	}),
	[setRiskCalcStatus.toString()]: (state, action) => ({
		...state,
		calcStatus: action.payload,
	}),
	[getLog.toString()]: state => ({
		...state,
		log: undefined,
		isLoading: true,
	}),
	[getLogResolved.toString()]: (state, action) => {
		const log: LogReply = action.payload;
		if (log.startTime && log.startTime != '')
			log.startTime = new Date(Date.parse(log.startTime)).toISOString();
		if (log.endTime && log.endTime != '')
			log.endTime = new Date(Date.parse(log.endTime)).toISOString();
		return {
			...state,
			log: log,
			isLoading: false,
			riskConfig: {
				...state?.riskConfig,
				riskSetting: {
					...state?.riskConfig?.riskSetting,
					LastRun: log.startTime,
					lastRunCompleted:
						// Update the lastRunCompleted time only if we completed the calc otherwise just keep it
						log.calcStatus === 'risk-calc-complete'
							? log.startTime
							: state.riskConfig.riskSetting.lastRunCompleted,
				},
			},
		};
	},
	[getLogRejected.toString()]: state => ({
		...state,
		isLoading: false,
	}),
	[changeFilterSelect.toString()]: (state, action) => ({
		...state,
		dropdownFilters: AM_COMMON.updateChangeFilterSelect(
			state.dropdownFilters,
			action.payload,
		),
	}),
	[changeFiltersSelect.toString()]: (state, action) => {
		const columns = action.payload ?? [];

		let dropdownFilters = { ...state.dropdownFilters };
		dropdownFilters.filters = {}; // Cleanup the filters

		if (columns.length !== 0) {
			dropdownFilters = AM_COMMON.changeMultipleFiltersSelect(
				dropdownFilters,
				columns,
			);
		} else {
			dropdownFilters.query = '';
		}

		return {
			...state,
			dropdownFilters: dropdownFilters,
		};
	},
	[updateColumnFilterSelectOptions.toString()]: (state, action) => ({
		...state,
		dropsownFilters: AM_COMMON.updateColumnFilterSelectOptions(
			state.dropdownFilters,
			action.payload,
		),
	}),
	[translate.toString()]: (state, action) => ({
		...state,
		dropsownFilters: AM_COMMON.updateTranslations(
			state.dropdownFilters,
			action.payload,
		),
	}),
	[setRiskSetupDirty.toString()]: (state, action) => ({
		...state,
		isSetupDirty: action.payload,
		enablePreview: action.payload,
	}),
	[setRunCompleteReload.toString()]: (state, action) => ({
		...state,
		runCompleteReload: action.payload,
	}),
	[setRefreshList.toString()]: (state, action) => ({
		...state,
		refreshList: action.payload,
	}),
	[setRiskConfigNameError.toString()]: (state, action) => ({
		...state,
		riskConfigNameError: action.payload,
	}),
	[setRiskConfigIsCreating.toString()]: (state, action) => ({
		...state,
		isCreating: action.payload,
	}),
	[setRiskIsSaving.toString()]: (state, action) => ({
		...state,
		isSaving: action.payload,
	}),
	[deleteRisk.toString()]: state => ({
		...state,
		isDeleting: true,
	}),
	[setRiskIsDeleting.toString()]: (state, action) => ({
		...state,
		isDeleting: action.payload,
	}),

	[setRiskCountIsLoading.toString()]: (state, action) =>
		updateIsCountLoading(state, action.payload),
	[getRiskCountResolved.toString()]: (state, action) =>
		updateRiskCount(state, action.payload),
	[renameRiskCount.toString()]: (state, action) =>
		copyRiskCount(state, action.payload),
	[setOverallRisk.toString()]: (state, action) => ({
		...state,
		overallRisk: action.payload,
	}),
	//===risk matrix actions====
	[setUpdatedRanges.toString()]: (state, action) => {
		return {
			...state,
			riskMatrix: {
				...state?.riskMatrix,
				grades: action.payload,
			},
		};
	},
	[setDidMatrixUpdate.toString()]: (state, action) => {
		return {
			...state,
			didMatrixUpdate: action.payload,
		};
	},
	[setUpdatedScores.toString()]: (state, action) => {
		return {
			...state,
			riskMatrix: {
				...state?.riskMatrix,
				grid: action.payload,
			},
		};
	},
	[setRiskType.toString()]: (state, action) => {
		return {
			...state,
			riskConfig: {
				...state?.riskConfig,
				riskSetting: {
					...state?.riskConfig?.riskSetting,
					riskType: action.payload,
				},
			},
			riskType: action.payload,
		};
	},
	[setEnablePreview.toString()]: (state, action) => {
		return {
			...state,
			enablePreview: action.payload,
		};
	},
	[setCancellPreview.toString()]: (state, action) => {
		return {
			...state,
			cancelPreview: action.payload,
		};
	},
	[setIsPreviewLoading.toString()]: (state, action) => {
		return {
			...state,
			isPreviewLoading: action.payload,
		};
	},
	[setIsPreviewOnLoad.toString()]: (state, action) => {
		return {
			...state,
			isPreviewOnLoad: action.payload,
		};
	},
	[setMatrixPreviewStatus.toString()]: (state, action) => {
		return {
			...state,
			previewStatus: action.payload,
		};
	},
	[setRiskMatrixAfterPreview.toString()]: (state, action) => {
		return {
			...state,
			riskMatrix: action.payload,
		};
	},
	[setRefreshMatrixGrid.toString()]: (state, action) => {
		return {
			...state,
			refreshMatrixGrid: action.payload,
		};
	},
	[setEditedCellsList.toString()]: (state, action) => {
		return {
			...state,
			editedCellsList: [...state.editedCellsList, action.payload],
		};
	},
	[setOnlyGradesUpdated.toString()]: (state, action) => {
		return {
			...state,
			isOnlyGradesUpdated: action.payload,
		};
	},
	[clearMatrixUpdate.toString()]: (state, action) => {
		let tempMatrix = { ...defaultRiskMatrix };

		if (
			state.riskConfig.riskSetting?.matrix?.grades &&
			state.riskConfig.riskSetting?.matrix?.grid?.length > 0
		) {
			let tempGrade = state.riskConfig.riskSetting?.matrix?.grades;
			if (action.payload) {
				tempGrade = defaultRiskMatrix.grades;
			}
			const tempGrid: RiskMatrixGridObj[] =
				state.riskConfig.riskSetting?.matrix?.grid.map(entry => {
					return {
						pipeCount: 0,
						pipeLength: 0,
						grade: entry.grade,
						position: entry.position,
					};
				});
			tempMatrix = {
				grades: tempGrade,
				grid: tempGrid,
			};
		}
		return {
			...state,
			riskMatrix: { ...tempMatrix },
			previewStatus: '',
			isPreviewOnLoad: false,
		};
	},
	[resetRiskResolved.toString()]: state => {
		return {
			...state,
			riskRunning: false,
		};
	},
	//==========
};

export const riskReducer = createReducer(initState, reducer);
